import { Button, Spinner } from 'alurkerja-ui';
import React, { Fragment, createContext, useState } from 'react';
import { RefreshCcw } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { Section } from '@/components/shared/section/Section';
import { Empty } from '@/pages/others/Empty';
import { useParams } from 'react-router-dom';
import { TaskDetail } from './TaskDetail';
import { TasklistService } from '@/api/tasklist';
import ModalStartProcess from './ModalStartProcess';
import { CardTask } from '@/components/shared/card/CardTask';
import { toTitleCase } from '@/utils/functions/toTitleCase';
import { NotFound } from '@/pages/others/NotFound';
import DiagramTask from '@/components/tasklist/tasks/DiagramTask';

export type anyContext = {
  data: string;
  processDefinition: string;
  resetSelectedTask?: () => void;
  refetchTasklistMyTask?: () => void;
  setActiveTaskId?: React.Dispatch<React.SetStateAction<string | null>>;
};

type ITMyTaskActionContextask = {
  refetchTasklistMyTask?: () => void;
};

export const TasklistMyTaskContext = createContext<anyContext>({
  data: '',
  processDefinition: '',
});

export const MyTaskActionContext = createContext<ITMyTaskActionContextask>({} as ITMyTaskActionContextask);

export const TasklistMyTask = () => {
  const { processName } = useParams<{ processName: string }>();

  const { data: tasks, isFetching, refetch, error } = TasklistService.useGetAllTask({ processDefinition: processName, });

  const { mutate, data } = useMutation({
    mutationFn: async (selected?: any): Promise<any | undefined> => {
      return selected;
    },
  });

  // Active card
  const [activeTaskId, setActiveTaskId] = useState<string | null>(null);

  const resetSelectedTask = () => {
    setActiveTaskId(null);
    mutate(undefined);
  };


  if (isFetching) {
    return (
      <div className="flex items-center gap-2.5">
        <Spinner className="text-main-blue-alurkerja" />
        Loading...
      </div>
    );
  }

  if (error?.response.status === 403) {
    return (
      <Section title="403 Forbidden">
        <div className="flex flex-col items-center gap-1 p-10">
          <span className="text-lg font-bold">{error.response.status} Forbidden</span>
          <div>{error.response.data.message}</div>
        </div>
      </Section>
    );
  }

  return (
    <Section
      title={`${toTitleCase(processName || '')}`}
      action={() => {
        return (
          <ModalStartProcess
            refetch={refetch}
            label={'Start Process ' + `${toTitleCase(processName || '')}`}
            processDefinitionKey={processName!}
            taskDefinitionKey="STARTPROCESS"
          ></ModalStartProcess>
        );
      }}
    >
      <MyTaskActionContext.Provider value={{ refetchTasklistMyTask: refetch }}>
        <div className="flex px-2 lg:px-6">
          <div className="block w-full sm:w-1/3 xl:w-1/5 pr-0 sm:pr-2 pt-4">
            <div className="h-[80vh] overflow-hidden sticky top-20 space-y-2.5">
              <Button icon={<RefreshCcw size={12} />} onClick={() => refetch()} isBlock={false}>
                Refresh
              </Button>
              {isFetching ? (
                <div className="flex items-center gap-2.5">
                  <Spinner className="text-main-blue-alurkerja" />
                  Getting Your Tasks...
                </div>
              ) : (
                <div className="max-h-[72vh] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-alurkerja-3 scrollbar-thumb-rounded-xl space-y-2 pb-12 sm:pb-4">
                  {tasks && tasks.length > 0 ? (
                    tasks.map(task => (
                      <Fragment key={task.id}>
                        <CardTask
                          isActive={activeTaskId === task.id}
                          onClick={() => {
                            setActiveTaskId(task.id);
                            mutate(task);
                          }}
                          task={task}
                        />
                      </Fragment>
                    ))
                  ) : (
                    <div className="flex flex-col items-center justify-items-center gap-2.5 border rounded-xl py-4 px-11">
                      <svg xmlns="http://www.w3.org/2000/svg" width="39" height="37" viewBox="0 0 39 37" fill="none">
                        <path
                          d="M0 33.3C0 34.2813 0.410892 35.2224 1.14228 35.9163C1.87368 36.6102 2.86566 37 3.9 37H9.75V33.3H3.9V27.75H0V33.3ZM39 33.3V27.75H35.1V33.3H29.25V37H35.1C36.1343 37 37.1263 36.6102 37.8577 35.9163C38.5891 35.2224 39 34.2813 39 33.3ZM35.1 3.7V9.25H39V3.7C39 2.7187 38.5891 1.77759 37.8577 1.08371C37.1263 0.38982 36.1343 0 35.1 0H29.25V3.7H35.1ZM0 3.7V9.25H3.9V3.7H9.75V0H3.9C2.86566 0 1.87368 0.38982 1.14228 1.08371C0.410892 1.77759 0 2.7187 0 3.7ZM15.6 37H23.4V33.3H15.6V37ZM15.6 3.7H23.4V0H15.6V3.7ZM35.1 22.2H39V14.8H35.1V22.2ZM0 22.2H3.9V14.8H0V22.2Z"
                          fill="#828282"
                        />
                      </svg>
                      <div className="text-xs">Belum Ada Task Yang Di Assign</div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="w-0 overflow-hidden sm:w-2/3 xl:w-4/5 pl-0 sm:pl-2">
            {data ? (
              <TasklistMyTaskContext.Provider
                value={{
                  data: data.id,
                  processDefinition: processName!,
                  refetchTasklistMyTask: refetch,
                  resetSelectedTask,
                  setActiveTaskId,
                }}
              >
                <TaskDetail></TaskDetail>
              </TasklistMyTaskContext.Provider>
            ) : (
              <DiagramTask
              show={true}
              processKey={processName??""}
              taskKey={""}
              />
            )}
          </div>
        </div>
      </MyTaskActionContext.Provider>
    </Section>
  );
};
